//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { Doc, PersonId, Ref, Space, Status } from '@hcengineering/core'
import type { IntlString } from '@hcengineering/platform'
import task from '.'

/*
  Avocado workflow (Jira-like), one per project. A project's board, its
  status pickers and the server all read the same `ProjectWorkflow` document:
  which stages are on and in what order, which stages may follow each one,
  who may move a task in, and what must be filled first. A project that was
  never set up has every shared stage on and no rules.

  Statuses themselves stay shared documents; a stage made inside one project
  carries `scope` (that project) and appears nowhere else.
*/

/** Who may move a task into a stage. */
export type WorkflowRole = 'all' | 'participants' | 'admins'

/** Fields a task must have filled before it may enter a stage. */
export type WorkflowField = 'assignee' | 'dueDate' | 'estimation' | 'component' | 'milestone'

export const workflowFields: WorkflowField[] = ['assignee', 'dueDate', 'estimation', 'component', 'milestone']

export const workflowRoles: WorkflowRole[] = ['all', 'participants', 'admins']

/** One stage of a project's workflow and its rules. */
export interface WorkflowStage {
  _id: Ref<Status>
  /** Switched off: offered nowhere in this project, and no task may enter it. */
  disabled?: boolean
  /** Only the stages in `transitions` may follow this one; otherwise any. */
  restricted?: boolean
  transitions?: Array<Ref<Status>>
  enterBy?: WorkflowRole
  required?: WorkflowField[]
}

/** A project's workflow: its stages in board order. Lives in the project's space. */
export interface ProjectWorkflow extends Doc {
  project: Ref<Space>
  stages: WorkflowStage[]
}

/** What a rule needs to know: the project, and its workflow if it has one. */
export interface WorkflowContext {
  space?: Ref<Space>
  workflow?: Pick<ProjectWorkflow, 'stages'>
}

/** Apply scope of "move the tasks out of a stage being switched off": past the transitions. */
export const WORKFLOW_MIGRATE_SCOPE = 'tracker-workflow-migrate'

/** The project a stage was made in (none: a shared stage). */
export function statusScope (status: Status | undefined): Ref<Space> | undefined {
  return (status as (Status & { scope?: Ref<Space> }) | undefined)?.scope
}

export function stageOf (ctx: WorkflowContext, status: Ref<Status> | undefined): WorkflowStage | undefined {
  if (status === undefined) return undefined
  return ctx.workflow?.stages.find((it) => it._id === status)
}

export function isStatusEnabled (ctx: WorkflowContext, status: Status | undefined): boolean {
  if (status === undefined) return false
  const scope = statusScope(status)
  if (scope !== undefined && ctx.space !== undefined && scope !== ctx.space) return false
  if (ctx.workflow === undefined) return true
  const stage = stageOf(ctx, status._id)
  return stage !== undefined && stage.disabled !== true
}

/** May a task go from `from` (none: a new task) to `to`? Staying put is always allowed. */
export function canTransition (ctx: WorkflowContext, from: Status | undefined, to: Status | undefined): boolean {
  if (to === undefined) return false
  if (from !== undefined && from._id === to._id) return true
  if (!isStatusEnabled(ctx, to)) return false
  if (from === undefined) return true
  const stage = stageOf(ctx, from._id)
  return stage?.restricted !== true || (stage.transitions ?? []).includes(to._id)
}

/** The person making a change. */
export interface WorkflowActor {
  /** Superadmin or Admin: passes every "who may" rule. */
  admin: boolean
  person?: Ref<Doc>
  socialIds: PersonId[]
}

/** The fields of a task the rules look at, as they would be after the change. */
export interface WorkflowTask {
  assignee?: Ref<Doc> | null
  reporter?: Ref<Doc> | null
  createdBy?: PersonId
  [key: string]: any
}

export function mayEnterStatus (ctx: WorkflowContext, to: Status, doc: WorkflowTask, actor: WorkflowActor): boolean {
  const by = stageOf(ctx, to._id)?.enterBy ?? 'all'
  if (by === 'all' || actor.admin) return true
  if (by === 'admins') return false
  const me = actor.person
  return (
    (me !== undefined && (doc.assignee === me || doc.reporter === me)) ||
    (doc.createdBy !== undefined && actor.socialIds.includes(doc.createdBy))
  )
}

function isEmpty (value: any): boolean {
  return value === undefined || value === null || value === '' || value === 0
}

export function missingWorkflowFields (ctx: WorkflowContext, doc: WorkflowTask, to: Status): WorkflowField[] {
  return (stageOf(ctx, to._id)?.required ?? []).filter((field) => isEmpty(doc[field]))
}

/** Why a change is refused: a message and its params (`fields` holds field keys, comma separated). */
export interface WorkflowProblem {
  message: IntlString
  params: Record<string, string>
}

/**
 * The one check for a status change in a project. `from` is undefined for a
 * new task; `doc` is the task as it would be after the change.
 */
export function statusChangeProblem (
  ctx: WorkflowContext,
  from: Status | undefined,
  to: Status,
  doc: WorkflowTask,
  actor: WorkflowActor,
  checkTransition: boolean = true
): WorkflowProblem | undefined {
  if (from !== undefined && from._id === to._id) return undefined
  if (!isStatusEnabled(ctx, to)) {
    return { message: task.string.WorkflowStageDisabled, params: { stage: to.name } }
  }
  if (checkTransition && !canTransition(ctx, from, to)) {
    return { message: task.string.WorkflowTransitionNotAllowed, params: { from: from?.name ?? '', stage: to.name } }
  }
  if (!mayEnterStatus(ctx, to, doc, actor)) {
    return {
      message:
        stageOf(ctx, to._id)?.enterBy === 'admins'
          ? task.string.WorkflowOnlyAdmins
          : task.string.WorkflowOnlyParticipants,
      params: { stage: to.name }
    }
  }
  const missing = missingWorkflowFields(ctx, doc, to)
  if (missing.length > 0) {
    return { message: task.string.WorkflowFieldsRequired, params: { stage: to.name, fields: missing.join(', ') } }
  }
  return undefined
}

/** `statuses` in the project's board order (stages it never listed keep their place after). */
export function inWorkflowOrder<T extends { _id: Ref<Status> }> (ctx: WorkflowContext, statuses: T[]): T[] {
  const stages = ctx.workflow?.stages
  if (stages === undefined) return statuses
  const rank = new Map(stages.map((it, i) => [it._id, i]))
  return statuses
    .map((it, i) => ({ it, i }))
    .sort((a, b) => (rank.get(a.it._id) ?? stages.length + a.i) - (rank.get(b.it._id) ?? stages.length + b.i))
    .map(({ it }) => it)
}

// Getters: this module is loaded while `task` (index.ts) is still being defined.
export const workflowFieldLabels: Record<WorkflowField, IntlString> = {
  get assignee () {
    return task.string.WorkflowFieldAssignee
  },
  get dueDate () {
    return task.string.WorkflowFieldDueDate
  },
  get estimation () {
    return task.string.WorkflowFieldEstimation
  },
  get component () {
    return task.string.WorkflowFieldComponent
  },
  get milestone () {
    return task.string.WorkflowFieldMilestone
  }
}

export const workflowRoleLabels: Record<WorkflowRole, IntlString> = {
  get all () {
    return task.string.WorkflowRoleAll
  },
  get participants () {
    return task.string.WorkflowRoleParticipants
  },
  get admins () {
    return task.string.WorkflowRoleAdmins
  }
}
